import { CommonModule } from "@angular/common";
import {
  Component,
  computed,
  EventEmitter,
  inject,
  Output,
  signal,
  DestroyRef,
} from "@angular/core";
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { NgIcon, provideIcons } from "@ng-icons/core";
import { lucideCheck, lucideX } from "@ng-icons/lucide";
import {
  HlmButton,
  HlmIconImports,
  HlmInput,
  HlmLabel,
  HlmSpinnerImports,
  O2PasswordInputComponent,
} from "@o2-fe/ui";
import {
  AuthenticateErrorEnum,
  AuthenticateStateEnum,
  SignUpEnum,
} from "../../enums/authenticate.enum";
import { CognitoSignUpResult } from "../../interfaces/auth.interfaces";
import { AuthService } from "../../services/auth.service";
import {
  passwordStrengthValidator,
  passwordMatchValidator,
} from "../../validators/password.validators";

@Component({
  selector: "app-sign-up",
  standalone: true,
  imports: [
    CommonModule,
    HlmButton,
    NgIcon,
    HlmIconImports,
    HlmInput,
    FormsModule,
    ReactiveFormsModule,
    HlmLabel,
    HlmSpinnerImports,
    O2PasswordInputComponent,
  ],
  providers: [provideIcons({ lucideCheck, lucideX })],
  host: {
    class: "block",
  },
  templateUrl: "./sign-up.component.html",
})
export class SignUpComponent {
  private readonly _authService: AuthService = inject(AuthService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _router = inject(Router);

  protected readonly signUpEnum = SignUpEnum;
  protected readonly isLoading = signal(false);
  protected readonly signUpState = signal<SignUpEnum | undefined>(undefined);
  protected readonly errorMessage = signal<string | undefined>(undefined);
  protected readonly hasError = computed(
    () => this.signUpState() === SignUpEnum.FAIL || Boolean(this.errorMessage()),
  );

  @Output()
  protected readonly signUpEvent = new EventEmitter<AuthenticateStateEnum>();

  protected readonly form = new FormGroup(
    {
      email: new FormControl<string>("", {
        nonNullable: true,
        validators: [Validators.required, Validators.email],
      }),
      password: new FormControl<string>("", {
        nonNullable: true,
        validators: [Validators.required, passwordStrengthValidator],
      }),
      confirmPassword: new FormControl<string>("", {
        nonNullable: true,
        validators: [Validators.required],
      }),
    },
    { validators: passwordMatchValidator },
  );

  /** Password validation state for UI feedback */
  protected get passwordValidation(): {
    minLength: boolean;
    uppercase: boolean;
    lowercase: boolean;
    number: boolean;
    specialChar: boolean;
  } {
    const password = this.form.get("password")?.value ?? "";
    return {
      minLength: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      specialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
    };
  }

  /** Check if password and confirmPassword match */
  protected get passwordsMatch(): boolean {
    const password = this.form.get("password")?.value ?? "";
    const confirmPassword = this.form.get("confirmPassword")?.value ?? "";
    return password.length > 0 && password === confirmPassword;
  }

  /**
   * Handles user registration.
   * On success or if user exists but not confirmed, emits CONFIRM_SIGN_UP
   * to transition to OTP confirmation screen.
   * AuthService.signUp() already stores credentials for auto-login after OTP.
   */
  protected signUp(): void {
    this.errorMessage.set(undefined);
    this.signUpState.set(undefined);

    // Mark all fields as touched to show validation errors
    this.form.markAllAsTouched();

    // Check form validity before proceeding
    if (!this.form.valid) {
      this.errorMessage.set("Compila tutti i campi correttamente");
      return;
    }

    if (this.form.hasError("passwordMismatch")) {
      this.errorMessage.set("Le password non coincidono");
      return;
    }

    this.isLoading.set(true);
    const { email, password } = this.form.getRawValue();

    this._authService
      .signUp(email, password)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (result: CognitoSignUpResult) => {
          if (result.type === SignUpEnum.SUCCESS) {
            this.signUpEvent.emit(AuthenticateStateEnum.CONFIRM_SIGN_UP);
          }
          this.isLoading.set(false);
        },
        error: (error: CognitoSignUpResult) => {
          this.isLoading.set(false);
          // If user already exists, redirect to login page
          if (
            error.result?.name ===
            (AuthenticateErrorEnum.USERNAME_EXISTS_EXCEPTION as string)
          ) {
            void this._router.navigate(["/"]);
            return;
          }
          this.signUpState.set(SignUpEnum.FAIL);
        },
      });
  }

  /**
   * Navigates to login page.
   * Uses router navigation instead of state change to go to root route.
   */
  protected onSignInClick(): void {
    void this._router.navigate(["/"]);
  }
}
